import React from 'react';
import {Form, Row, Col} from 'react-bootstrap';

interface RangeFieldProps {
  name: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  placeholder?: string;
  isRequired?: boolean;
  error?: string;
  touched?: boolean;
  disabled?: boolean;
  showValue?: boolean;
  showMinMax?: boolean;
  unit?: string;
  marks?: { value: number; label: string }[];
}

const RangeField: React.FC<RangeFieldProps> = ({
    name,
    value,
    onChange,
    min = 0,
    max = 100,
    step = 1,
    isRequired,
    error,
    touched,
    disabled = false,
    showValue = true,
    showMinMax = true,
    unit = '',
    marks = [],
}) => {
    const hasError = touched && !!error;

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onChange(Number(event.target.value));
    };

    const percentage = ((value - min) / (max - min)) * 100;

    return (
        <div>
            <Row className="align-items-center">
                <Col xs={showValue ? 10 : 12}>
                    <div className="position-relative">
                        <Form.Range
                            name={name}
                            value={value}
                            onChange={handleChange}
                            min={min}
                            max={max}
                            step={step}
                            disabled={disabled}
                            className={hasError ? 'is-invalid' : ''}
                            style={{
                                '--bs-range-thumb-bg': hasError ? '#dc3545' : '#0d6efd'
                            } as React.CSSProperties}
                        />

                        {/* Marks on the range */}
                        {marks.length > 0 && (
                            <div className="d-flex justify-content-between mt-2 px-1">
                                {marks.map((mark) => (
                                    <div
                                        key={mark.value}
                                        className="d-flex flex-column align-items-center"
                                        style={{flex: '1'}}
                                    >
                                        <div
                                            className="bg-secondary rounded-circle mb-1"
                                            style={{width: '4px', height: '4px'}}
                                        />
                                        <small className="text-muted text-center" style={{ 
                                            fontSize: '0.75rem',
                                            whiteSpace: 'nowrap'
                                        }}>
                                            {mark.label}
                                        </small>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {showMinMax && (
                        <div className="d-flex justify-content-between mt-1">
                            <small className="text-muted">{min}{unit}</small>
                            <small className="text-muted">{max}{unit}</small>
                        </div>
                    )}
                </Col>

                {showValue && (
                    <Col xs={2}>
                        <Form.Control
                            type="number"
                            value={value}
                            onChange={handleChange}
                            min={min}
                            max={max}
                            step={step}
                            disabled={disabled}
                            size="sm"
                            className={`text-center ${hasError ? 'is-invalid' : ''}`}
                        />
                        {unit && (
                            <small className="text-muted d-block text-center">{unit}</small>
                        )}
                    </Col>
                )}
            </Row>

            {hasError && (
                <Form.Control.Feedback type="invalid" style={{display: 'block'}}>
                    {error}
                </Form.Control.Feedback>
            )}

            {isRequired && (
                <Form.Text className="text-muted">
                    <span className="text-danger">*</span> Required
                </Form.Text>
            )}
        </div>
    );
};

export default RangeField;
